import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Settings from "components/dashboard/Settings";
import ApprovalPage from "pages/dashboard/global-admin/ApprovalPage";
import AssistantManagerPage from "pages/dashboard/global-admin/AssistantManagerPage";
import UsageMonitorPage from "pages/dashboard/global-admin/UsageMonitorPage";
import RegionalAdminsPage from "pages/dashboard/global-admin/RegionalAdminsPage";
import HipaaComplianceCalendarPage from "pages/dashboard/global-admin/HipaaComplianceCalendarPage";
import GlobalTciManagersPage from "pages/dashboard/global-admin/GlobalTciManagersPage";
import GlobalTciManagerDashboard from "pages/dashboard/global-tci-manager/GlobalTciManagerDashboard";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import PageGuideAdminPage from "pages/dashboard/admin/pageGuide/PageGuideAdminPage";
import AllFilesPage from "pages/dashboard/admin/AllFilesPage";
import OrganizationsPage from "pages/admin/OrganizationsPage";
import GlobalReadinessPage from "pages/admin/GlobalReadinessPage";
import IpaReviewPage from "pages/admin/IpaReviewPage";
import RetiredChartMindRoute from "components/common/RetiredChartMindRoute";
import ChartMindPlusPage from "pages/chartmindPlus/ChartMindPlusPage";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";
import ChartMindPlusDifferentialPage from "pages/chartmindPlus/ChartMindPlusDifferentialPage";
import PeerViewPage from "pages/dashboard/professional/PeerViewPage";
// App-launcher preview: role-scoped product landing pages exposed to the
// global admin so launcher destinations can be opened and confirmed.
import { MasterChartDashboardRoutes } from "routes/MasterChartDashboardRoutes";
import { SavoirCareBoardPage } from "features/savoirCare";
import InterpreterRequestsPage from "pages/dashboard/facility/InterpreterRequestsPage";
import AnalyticsPage from "pages/dashboard/admin/AnalyticsPage";
import LoopAnalyticsPage from "pages/dashboard/admin/LoopAnalyticsPage";
import ReadinessDashboard from "pages/dashboard/readiness/ReadinessDashboard";
import InstitutionReportsPage from "pages/dashboard/reports/InstitutionReportsPage";
import VideoLibraryPage from "pages/dashboard/institutionRollout/VideoLibraryPage";
import InstitutionBrandPage from "pages/dashboard/institutionRollout/InstitutionBrandPage";
import DistributePage from "pages/dashboard/institutionRollout/DistributePage";
import TciManagerDashboard from "pages/dashboard/tcimanager/TciManagerDashboard";
import CareGapsPage from "pages/dashboard/individual/CareGapsPage";
import ZipSlotBiddingPage from "pages/dashboard/zipSlotAds/ZipSlotBiddingPage";
import BtybAuctionPage from "pages/dashboard/btyb/BtybAuctionPage";
import ResultsCommsPage from "pages/dashboard/individual/ResultsCommsPage";
import ChatPage from "components/chat_new/ChatPage";
import RequirePlatformAdmin from "components/appLauncher/RequirePlatformAdmin";
import DashboardLayout from "components/dashboard/DashboardLayout";
import { useAuth } from "hooks/useAuth";
import { PromptsManagerProvider } from "context/PromptsManager";
import { ChartMindSessionProvider } from "context/ChartMindSession";

/**
 * Routes for platform super-admins (custom claim admin + role global_admin).
 * Includes ChartMind for testing the same flows as professional users.
 * ML1: `/medical-library` reuses ChatPage behind RequirePlatformAdmin.
 * G2d: CareMap Ads and BTYB mount here behind RequirePlatformAdmin.
 * The index still redirects to `/dashboard/prompts`.
 */
const GlobalAdminRoutes = () => {
  const { user, subscription } = useAuth();
  const location = useLocation();
  const path = location.pathname;
  const isChartMindSessionList = /\/chartmind\/sessions\/?$/.test(path);
  const chartMindFullWidth =
    (path.includes("/chartmind") || path.includes("/peerview")) &&
    !isChartMindSessionList;
  // ChatPage is a full-viewport thread. Keep it out of the narrow page column.
  const isMedicalLibrary = /\/medical-library\/?$/.test(path);

  return (
    <DashboardLayout fullWidth={chartMindFullWidth || isMedicalLibrary}>
      <PromptsManagerProvider>
        <ChartMindSessionProvider>
          <Routes>
            <Route
              path="/"
              element={<Navigate to="/dashboard/prompts" replace />}
            />
            <Route
              path="/settings"
              element={<Settings uid={user?.uid} subscription={subscription} />}
            />
            <Route path="/admin/approval" element={<ApprovalPage />} />
            <Route
              path="/admin/regional-admins"
              element={<RegionalAdminsPage />}
            />
            <Route
              path="/admin/global-tci-managers"
              element={<GlobalTciManagersPage />}
            />
            <Route path="/global-tci-manager" element={<GlobalTciManagerDashboard />} />
            <Route
              path="/admin/hipaa-compliance"
              element={<HipaaComplianceCalendarPage />}
            />
            <Route path="/admin/assistants" element={<AssistantManagerPage />} />
            <Route path="/admin/usage" element={<UsageMonitorPage />} />
            <Route path="/admin/organizations" element={<OrganizationsPage />} />
            <Route path="/admin/readiness" element={<GlobalReadinessPage />} />
            <Route path="/admin/ipa" element={<IpaReviewPage />} />
            <Route path="/prompts" element={<PromptsManagerPage />} />
            <Route path="/page-guide" element={<PageGuideAdminPage />} />
            <Route path="/files" element={<AllFilesPage />} />
            <Route path="/diskusage" element={<Navigate to="/dashboard/files" replace />} />
            <Route
              path="/chartmind"
              element={
                <RetiredChartMindRoute />
              }
            />
            <Route
              path="/chartmind-plus"
              element={
                <RequireLauncherApp appId="chartmind-plus">
                  <ChartMindPlusPage />
                </RequireLauncherApp>
              }
            />
            <Route
              path="/chartmind-plus/differential"
              element={
                <RequireLauncherApp appId="chartmind-plus">
                  <ChartMindPlusDifferentialPage />
                </RequireLauncherApp>
              }
            />
            <Route path="/chartmind/sessions" element={<RetiredChartMindRoute />} />
            <Route path="/peerview" element={<PeerViewPage />} />
            <Route
              path="/chartmind/:sessionId"
              element={
                <RetiredChartMindRoute />
              }
            />
            {/* App-launcher preview routes — let the global admin open the
                role-scoped product landing pages to confirm launcher targets.
                MasterChart = health-records, SavoirCare = savoircare,
                LoopManager = loop-analytics (or readiness),
                FusionLead = reports (or tci-manager / analytics). */}
            {MasterChartDashboardRoutes()}
            <Route path="/savoircare" element={<SavoirCareBoardPage />} />
            <Route
              path="/interpreter-requests"
              element={<InterpreterRequestsPage />}
            />
            <Route path="/loop-analytics" element={<LoopAnalyticsPage />} />
            <Route path="/readiness" element={<ReadinessDashboard />} />
            <Route path="/reports" element={<InstitutionReportsPage />} />
            <Route path="/institution-rollout" element={<VideoLibraryPage />} />
            <Route
              path="/institution-rollout/brand"
              element={<InstitutionBrandPage />}
            />
            <Route
              path="/institution-rollout/distribute"
              element={<DistributePage />}
            />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/tci-manager" element={<TciManagerDashboard />} />
            <Route
              path="/care-gaps"
              element={
                <RequirePlatformAdmin>
                  <CareGapsPage />
                </RequirePlatformAdmin>
              }
            />
            <Route
              path="/results"
              element={
                <RequirePlatformAdmin>
                  <ResultsCommsPage />
                </RequirePlatformAdmin>
              }
            />
            <Route
              path="/medical-library"
              element={
                <RequirePlatformAdmin>
                  <ChatPage assistantType="basic-medical-library" />
                </RequirePlatformAdmin>
              }
            />
            <Route
              path="/advertising/slots"
              element={
                <RequirePlatformAdmin>
                  <ZipSlotBiddingPage />
                </RequirePlatformAdmin>
              }
            />
            <Route
              path="/btyb"
              element={
                <RequirePlatformAdmin>
                  <BtybAuctionPage />
                </RequirePlatformAdmin>
              }
            />
            <Route
              path="*"
              element={<Navigate to="/dashboard/prompts" replace />}
            />
          </Routes>
        </ChartMindSessionProvider>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default GlobalAdminRoutes;
